<template>
  <LoadingOverlay
    v-if="isFetching"
    :showLoadingOverlay="true"
  />

  <div
    v-else-if="!hasErrorFetching"
    class="userContainer"
  >
    <div class="verticalTabContainer">
      <PageScrolling
        hasWideBreakpoint
        :sections="$options.pageScrollingSections"
        shouldDetectScrolling
      >
        <template v-slot:#details>
          <div>
            <Card>
              <template v-slot:header>
                <HeadingMedium>
                  Details
                </HeadingMedium>
              </template>
              <template v-slot:body>
                <div>
                  <PasswordManagerUserInfo
                    :item="item"
                  />
                </div>
              </template>
            </Card>
            <PasswordManagerPasswordHealth
              class="passwordManagerPasswordHealth"
              :oldPasswords="item.oldPasswords"
              :reusedPasswords="item.reusedPasswords"
              :totalCount="item.passwordsCount"
              :weakPasswords="item.weakPasswords"
            />
          </div>
        </template>
        <template v-slot:#items>
          <div>
            <Card>
              <template v-slot:body>
                <div>
                  <PasswordManagerItemsTabs
                    :scopeId="userId"
                    :scopeType="$options.itemsScopeType"
                  />
                </div>
              </template>
            </Card>
          </div>
        </template>
        <template v-slot:#userdevices>
          <div>
            <Card>
              <template v-slot:header>
                <HeadingMedium>
                  Devices
                </HeadingMedium>
              </template>
              <template v-slot:body>
                <div>
                  <PwmUserAppsItemList
                    class="itemList"
                    :tableData="item.apps"
                  />
                </div>
              </template>
            </Card>
          </div>
        </template>
        <template
          v-slot:#logs
        >
          <Card>
            <template
              v-slot:header
            >
              <HeadingMedium
                class="activityLogsCardHeading"
              >
                Logs
                <ButtonLink
                  text="activity log"
                  textCase="lowercase"
                  @click="handleActivityLogClick"
                />
              </HeadingMedium>
            </template>
            <template
              v-slot:body
            >
              <PasswordManagerActivityLogs
                ref="passwordManagerActivityLogs"
                :apiFilters="activityLogApiFilter"
                :dropdownFilters="activityLogsDropdownFilter"
              />
            </template>
          </Card>
        </template>
      </PageScrolling>
    </div>
  </div>
</template>

<script>
import {
  ButtonLink,
  Card,
  HeadingMedium,
  PageScrolling,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { passwordManagerItemScope } from 'client/PasswordManager/PasswordManagerConstants';
import EventMetadata, { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import PasswordManagerActivityLogs from 'client/PasswordManager/components/PasswordManagerActivityLogs.vue';
import PasswordManagerItemsTabs from 'client/PasswordManager/Items/PasswordManagerItemsTabs.vue';
import PasswordManagerPasswordHealth from 'client/PasswordManager/components/PasswordManagerPasswordHealth.vue';
import PasswordManagerUserInfo from 'client/PasswordManager/Users/PasswordManagerUserInfo.vue';
import PwmUserAppsItemList from 'client/PasswordManager/Users/PwmUserAppsItemList.vue';

const PasswordManagerUserModel = 'PasswordManagerUserModel';

const { TabItem } = Types;

const pageScrollingSections = [
  TabItem({
    label: 'Details',
    key: '#details',
  }),
  TabItem({
    label: 'Items',
    key: '#items',
  }),
  TabItem({
    label: 'User Devices',
    key: '#userdevices',
  }),
  TabItem({
    label: 'Logs',
    key: '#logs',
  }),
];

export default {
  name: 'PasswordManagerUserDetails',

  components: {
    ButtonLink,
    Card,
    HeadingMedium,
    LoadingOverlay,
    PageScrolling,
    PasswordManagerActivityLogs,
    PasswordManagerUserInfo,
    PwmUserAppsItemList,
    PasswordManagerItemsTabs,
    PasswordManagerPasswordHealth,
  },

  pageScrollingSections,
  itemsScopeType: passwordManagerItemScope.user,

  props: {
    userId: {
      default: '',
      type: String,
    },
  },

  data() {
    return {
      isFetching: false,
      hasErrorFetching: false,
    };
  },

  computed: {
    ...mapGetters(PasswordManagerUserModel, [
      'item',
    ]),

    activityLogApiFilter() {
      return [EventMetadata[eventFields.user].filterCriteria(this.item.username)];
    },

    activityLogsDropdownFilter() {
      return [
        {
          field: eventFields.user,
          value: [this.item.username.toLowerCase()],
        },
      ];
    },
  },

  created() {
    this.fetchUser();
  },

  destroyed() {
    this.resetItem();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    ...mapActions(PasswordManagerUserModel, [
      'loadUser',
      'resetItem',
    ]),

    async fetchUser() {
      this.isFetching = true;

      try {
        await this.loadUser(this.userId);
      } catch (e) {
        this.hasErrorFetching = true;
        this.triggerErrorNotification({
          title: 'Error',
          message: 'Unable to retrieve user data. Please refresh the page and try again.',
        });
      } finally {
        this.isFetching = false;
      }
    },

    handleActivityLogClick() {
      this.$refs.passwordManagerActivityLogs.redirectToDirectoryInsights();
    },
  },
};
</script>

<style scoped>
.activityLogsCardHeading {
  display: flex;
  justify-content: space-between;
}

.passwordManagerPasswordHealth {
  margin-top: var(--jc-spacer);
}

.userContainer {
  --sidebar-width: 18rem;

  background: var(--jcWhite);
  height: 100%;
  min-height: 0;
  min-width: 0;
}

.verticalTabContainer {
  height: 100%;
}
</style>
